import { useLocation, useNavigate } from "react-router-dom";
import { ChangeEventStatus } from "src/generated/graphqlTypes";
import { WorkflowActionArgs } from "src/routes/projects/change-events/guided/workflows/types";
import { createChangeEventsUrl, createGuidedChangeEventUrl } from "src/RouteUrls";
import { SwapProductFormInput } from "./swapProductWorkflow";
import { useSwapProduct_SaveChangeEventMutation } from "./useSwapProductActions.generated";

/**
 * Defines the actions for the swap product workflows (in catalog and not in catalog).
 *
 * saveChangeEvent:
 * - If new, creates a new draft change event
 * - Saves the staged swaps, as one exhaustive list: whatever the form holds is what the change event ends up with.
 * A swap the user dropped is simply not in the list anymore, and the backend deletes it along with the line items it manages.
 * - Folds the saved change event back into the form, so the next advance doesn't re-send everything.
 */
export function useSwapProductActions(args: WorkflowActionArgs<SwapProductFormInput>) {
  const { form, projectId } = args;
  const navigate = useNavigate();
  const { state } = useLocation();
  const [saveChangeEvent] = useSwapProduct_SaveChangeEventMutation();

  return {
    saveChangeEvent: async ({ andExit }: { andExit?: boolean } = {}): Promise<void> => {
      if (!form.canSave()) return;
      // Nothing to save, but "exit" still has to exit, otherwise Save & Exit is a dead button on any
      // step the user only read through.
      if (!form.dirty) {
        if (andExit) navigate(createChangeEventsUrl(projectId));
        return;
      }

      const isNew = form.id.value === undefined;
      const result = await saveChangeEvent({
        variables: {
          input: {
            ...(isNew
              ? {
                  workflow: form.workflow.value,
                  projectStageId: form.projectStageId.value,
                  reasonId: form.reasonId.value,
                  type: form.type.value,
                  status: ChangeEventStatus.Draft,
                }
              : { id: form.id.value }),
            materialVariantSwaps: form.swaps.changedValue,
          },
        },
      });
      const savedChangeEvent = result.data?.saveChangeEvent.changeEvent;
      if (!savedChangeEvent) return;

      // Fold the saved change event back into the form: `update` re-runs the workflow's `mapToForm`
      // so the rows pick up their new swap ids and the form goes clean. Without it, the next advance
      // would re-send rows the backend already deleted and re-created.
      form.update(savedChangeEvent);
      form.revertChanges();

      // If new, replace the current url with the new change event id
      if (isNew) {
        navigate(createGuidedChangeEventUrl(projectId, savedChangeEvent.id), { replace: true, state });
      }
      if (andExit) {
        navigate(createChangeEventsUrl(projectId));
      }
    },
  };
}
